/**
 * Shared map utilities: base-map layers and UTM ↔ lon/lat helpers.
 *
 * Used by MapCollection, TunnelLineView, and TerrainModelsView.
 */
import TileLayer from "ol/layer/Tile";
import WMTS from "ol/source/WMTS";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import OSM from "ol/source/OSM";
import proj4 from "proj4";

/* ── Projection setup (run once) ── */
proj4.defs(
  "EPSG:25832",
  "+proj=utm +zone=32 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
);
proj4.defs(
  "EPSG:5110",
  "+proj=tmerc +lat_0=58 +lon_0=10.5 +k=1 +x_0=100000 +y_0=1000000 +ellps=GRS80 +towgs84=0,0,0,0,0,0,0 +units=m +no_defs",
);

/** Convert UTM Zone 32N (EPSG:25832) easting/northing to [lon, lat]. */
export function utm32nToLonLat(easting: number, northing: number): [number, number] {
  return proj4("EPSG:25832", "EPSG:4326", [easting, northing]) as [number, number];
}

/** Convert NTM Zone 10 (EPSG:5110) easting/northing to [lon, lat]. */
export function ntm10ToLonLat(easting: number, northing: number): [number, number] {
  return proj4("EPSG:5110", "EPSG:4326", [easting, northing]) as [number, number];
}

/** Convert [lon, lat] to UTM Zone 32N (EPSG:25832) [easting, northing]. */
export function lonLatToUtm32n(lon: number, lat: number): [number, number] {
  return proj4("EPSG:4326", "EPSG:25832", [lon, lat]) as [number, number];
}

/** Convert [lon, lat] to NTM Zone 10 (EPSG:5110) [easting, northing]. */
export function lonLatToNtm10(lon: number, lat: number): [number, number] {
  return proj4("EPSG:4326", "EPSG:5110", [lon, lat]) as [number, number];
}

/**
 * Detect whether coordinates are NTM Zone 10 or UTM Zone 32N based on magnitude,
 * and convert to [lon, lat] accordingly.
 * NTM10: easting ~100k, northing ~1M; UTM32N: easting ~500k, northing ~6.5M
 */
export function projectCoordsToLonLat(easting: number, northing: number): [number, number] {
  if (northing < 3_000_000) {
    // NTM Zone 10 (reduced northing ~1M range)
    return ntm10ToLonLat(easting, northing);
  }
  return utm32nToLonLat(easting, northing);
}

/* ── Kartverket WMTS tile grid ── */

const WMTS_REST_URL =
  "https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{TileMatrix}/{TileRow}/{TileCol}.png";

const RESOLUTIONS = Array.from(
  { length: 19 },
  (_, z) => 156543.03392804097 / Math.pow(2, z),
);

const MATRIX_IDS = Array.from({ length: 19 }, (_, z) =>
  String(z).padStart(2, "0"),
);

const ORIGIN: [number, number] = [-20037508.342789244, 20037508.342789244];

/**
 * Create the standard base-map layers: OSM globally + Kartverket topo on top.
 *
 * Kartverket tiles only cover Norway, so outside Norway you simply see OSM.
 * Returns an array of layers to add to the map (order: OSM first, Kartverket on top).
 */
export function createBaseLayers(): TileLayer[] {
  const osm = new TileLayer({ source: new OSM() });

  const kartverket = new TileLayer({
    source: new WMTS({
      url: WMTS_REST_URL,
      layer: "topo",
      matrixSet: "webmercator",
      format: "image/png",
      style: "default",
      requestEncoding: "REST",
      tileGrid: new WMTSTileGrid({
        origin: ORIGIN,
        resolutions: RESOLUTIONS,
        matrixIds: MATRIX_IDS,
        tileSize: 256,
      }),
      attributions: "\u00A9 Kartverket",
    }),
  });

  return [osm, kartverket];
}
